/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @nx/enforce-module-boundaries */
import { GetServerSideProps } from 'next';

import { PageRendererComponent, prePageCall } from '@marriott/mi-headless-utils';
import destinationSignatures from '../../signatures/operationSignaturesDestinations.json';
import {
  DESTINATIONS_ROUTE,
  DESTINATION_OFFERS_ROUTE,
  DESTINATION_OFFERS_SUFFIX,
  OFFERS_HUB_ROUTE,
} from '../../constants/routes';
const { SESSION_APP_DOMAIN_URL } = process.env;
import { getUserAgentType } from 'libs/mi-leisure-components/src/organisms/PropertiesList/utils/helper';
import { findComponentByType } from 'libs/mi-leisure-components/src/utils/DestinationUtils';
import { NEXT_PUBLIC_ENV_KEYS } from '@marriott/mi-leisure-components/constants';
import { getSubDirectoryLocale } from '@marriott/mi-leisure-components/utils';
import {
  buildOffersSearchVariables,
  getDefaultOffersSortBy,
  getOffersPageNo,
  getOffersPaginationModel,
  getOffersSearchQueryName,
  mapOffersSearchResponse,
  OFFERS_QUERY_PARAM_PAGE,
  resolveOffersSortBy,
  resolveOffersSearchMode,
  getFlexibleDateRange,
} from '@marriott/mi-offers-components';
import { fetchDestinationOffers } from '../api/utils/offersSearchApi';
import { buildDestinationAriesSearch, updateSessionObject } from '../api/utils/sessionApi';

const getDestinationOffersUri = (context: { resolvedUrl?: string; req?: { url?: string } }) => {
  const resolvedUrlString = context?.resolvedUrl ?? context?.req?.url ?? '';
  const requestPath = resolvedUrlString.split('?')[0];
  let resolvedDestinationUri = requestPath;

  if (requestPath === DESTINATION_OFFERS_ROUTE) {
    resolvedDestinationUri = DESTINATIONS_ROUTE;
  } else if (requestPath.startsWith(`${DESTINATION_OFFERS_ROUTE}/`)) {
    const destinationPath = requestPath.replace(DESTINATION_OFFERS_ROUTE, DESTINATIONS_ROUTE);
    const pathSegments = destinationPath.split('/');
    const lastSegmentIndex = pathSegments.length - 1;
    const lastSegment = pathSegments[lastSegmentIndex];

    if (lastSegment && !lastSegment.endsWith(DESTINATION_OFFERS_SUFFIX)) {
      pathSegments[lastSegmentIndex] = `${lastSegment}${DESTINATION_OFFERS_SUFFIX}`;
    }

    resolvedDestinationUri = pathSegments.join('/');
  }

  return resolvedDestinationUri;
};

function pageRenderer(props: any) {
  const url =
    props.model && props.model['canonical'] !== undefined && props.model['canonical'] !== ''
      ? new URL(props.model['canonical'], props.model['canonical'])
      : '';
  if (url !== '') {
    if (props.pageNo && props.pageNo !== '1') {
      url.searchParams.set(OFFERS_QUERY_PARAM_PAGE, props.pageNo);
    } else {
      url.searchParams.delete(OFFERS_QUERY_PARAM_PAGE);
    }
    const baseUrl = url.toString();
    props.model['canonical'] = baseUrl;
  }
  return <PageRendererComponent {...props} />;
}

//Any product specific page data apart from common data like session, datalayer
//can be returned from this function and the same is passed as props to the page Component
const destinationOffersSpecificData = async (
  context: any,
  _parallelCallsResponse: unknown,
  interceptorResponse: any
) => {
  const uri = getDestinationOffersUri(context);
  const currentLocale = interceptorResponse?.requestAttributes?.pageManagerRequest?.global?.localeKey?.replace(
    '_',
    '-'
  );
  const localeSubdirectory = currentLocale === 'en-US' ? '' : getSubDirectoryLocale(currentLocale);
  const localePrefix = localeSubdirectory ? `/${localeSubdirectory.replace(/^\/+/, '')}` : '';
  const redirectUrlWithSubdirectory = `${localePrefix}${OFFERS_HUB_ROUTE}`;

  const pageNo = getOffersPageNo(context?.req?.url, context?.req?.headers?.host);

  return {
    operationSignatures: destinationSignatures,
    uri,
    pageNo: String(pageNo),
    redirectUrI: redirectUrlWithSubdirectory,
    nextPublicEnvKeys: NEXT_PUBLIC_ENV_KEYS,
  };
};
const postAEMModelOperations = async (
  context: any,
  parallelCallsResponse: any,
  _interceptorResponse: any,
  model: any,
  _productSpecificProps: any
) => {
  const { req } = context;
  const { sessionData } = parallelCallsResponse;
  const requestId = req?.headers?.['x-request-id'] ?? '';
  const currentLocale = _interceptorResponse?.requestAttributes?.pageManagerRequest?.global?.localeKey?.replace(
    '_',
    '-'
  );
  const destinationMetaData = model?.destinationMetaData ?? {};
  const offersSearchResultsModel = findComponentByType(
    model?.cqItems,
    'mi-aem-offers-spa/components/offerssearchresults'
  );
  const searchFormModel = findComponentByType(model?.cqItems, 'mi-aem-leisure-spa/components/leisure-searchform');
  const viewPort = getUserAgentType(context?.req?.headers?.['user-agent']);
  const localeVal = model?.language;
  const urlLocale = localeVal === 'en-US' ? '/en-us' : model?.subDirectoryContextPath;
  const cleanedPath = context?.resolvedUrl?.split('?')[0].replace(/\.mi$/, '');
  const pathWithLocale =
    urlLocale && urlLocale.startsWith('/') ? `${urlLocale}${cleanedPath}` : `/${urlLocale}${cleanedPath}`;
  const currentDestination = urlLocale ? pathWithLocale : cleanedPath;

  const sessionCacheData = sessionData?.cacheData?.data;
  const ariesOffer = sessionCacheData?.AriesOffer ?? {};
  const hasdestinationChanged =
    currentDestination?.toLocaleLowerCase() !== ariesOffer?.AriesSessionDestination?.toLocaleLowerCase();

  const pageNo = getOffersPageNo(req?.url, req?.headers?.host);
  const cardsPerPage = Number(offersSearchResultsModel?.numberOfResultsPerPage ?? 40);
  const offset = (pageNo - 1) * cardsPerPage;
  const sessionToken = context?.req?.cookies?.sessionID ?? '';

  // SSR should only honor pagination from URL; other query params are client-side only.
  const offersSearchParams = new URLSearchParams();
  if (pageNo > 1) {
    offersSearchParams.set(OFFERS_QUERY_PARAM_PAGE, String(pageNo));
  }

  const { flexibleDefaultLengthOfStay, fromDateIso, toDateIso, fromDateQuery, toDateQuery } =
    getFlexibleDateRange(searchFormModel);
  offersSearchParams.set('fromDate', fromDateQuery);
  offersSearchParams.set('toDate', toDateQuery);

  // Destination-offers SSR resolves geo/area mode from destination metadata only.
  const resolvedSearchMode = resolveOffersSearchMode(
    {},
    {
      useDestinationMetaDataPayload: true,
      destinationMetaData,
    }
  );

  const selectedBrands = hasdestinationChanged ? [] : ariesOffer?.selectedBrands ?? [];
  const selectedOfferCategory = hasdestinationChanged ? [] : ariesOffer?.selectedOfferCategory ?? [];
  const selectedOfferType = hasdestinationChanged ? [] : ariesOffer?.selectedOfferType ?? [];
  const selectedFilterState = hasdestinationChanged ? [] : ariesOffer?.selectedFilterState ?? [];
  const selectedFilterCity = hasdestinationChanged ? [] : ariesOffer?.selectedFilterCity ?? [];
  const selectedFilterCountry = hasdestinationChanged ? [] : ariesOffer?.selectedFilterCountry ?? [];
  const fallbackSortBy = hasdestinationChanged
    ? getDefaultOffersSortBy(resolvedSearchMode)
    : ariesOffer?.sortBy ?? getDefaultOffersSortBy(resolvedSearchMode);
  const sortBy = resolveOffersSortBy({
    requestedSortBy: undefined,
    mode: resolvedSearchMode,
    fallbackSortBy,
  });

  const variables = buildOffersSearchVariables({
    acceptLanguage: currentLocale ?? 'en-US',
    urlParams: offersSearchParams,
    // Destination offers should always derive geo/area query inputs from destination metadata.
    // Keep session updates intact below for other components that may rely on AriesSearch.
    ariesSearchAddress: {},
    mode: resolvedSearchMode,
    limit: cardsPerPage,
    offset,
    sortBy,
    facets: {
      brandFacets: selectedBrands?.map((item: any) => item?.code),
      offerCategory: selectedOfferCategory?.map((item: any) => item?.code),
      offerType: selectedOfferType?.map((item: any) => item?.code),
      filterState: selectedFilterState?.map((item: any) => item?.code),
      filterCity: selectedFilterCity?.map((item: any) => item?.code),
      filterCountry: selectedFilterCountry?.map((item: any) => item?.code),
    },
    destinationMetaData,
  });
  const queryName = getOffersSearchQueryName(resolvedSearchMode);
  let mappedData: any = {
    cardCount: 0,
    offerCards: [],
    cityInData: '',
    facetsData: {
      availableBrands: [],
      availableOfferCategory: [],
      availableOfferType: [],
      availableFilterState: [],
      availableFilterCity: [],
      availableFilterCountry: [],
    },
  };
  let serverApiError = false;

  try {
    const {
      offersUrl,
      offersResponseData,
      serverApiError: offersApiError,
    } = await fetchDestinationOffers({
      requestId,
      acceptLanguage: currentLocale ?? 'en-US',
      queryName,
      variables,
    });

    serverApiError = offersApiError;
    console.log('[Destination Offers] BE API endpoint', offersUrl);
    console.log('[Destination Offers] BE API payload', JSON.stringify({ queryName, variables }));

    mappedData = mapOffersSearchResponse({
      uxlData: offersResponseData,
      model: offersSearchResultsModel,
      subdirectory: model?.subDirectoryContextPath,
    });
  } catch (error) {
    serverApiError = true;
    console.log('[Destination Offers] Offers API fetch failed', error);
  }

  const sortOptions = [
    {
      sortByOptionTitle: offersSearchResultsModel?.offersSortbyExpiringsoon,
      sortByOptionCode: 'BOOKING_END_DATE',
    },
    {
      sortByOptionTitle: offersSearchResultsModel?.offersSortbyNew,
      sortByOptionCode: 'BOOKING_START_DATE',
    },
  ];
  if (resolvedSearchMode?.searchByGeoLoc) {
    sortOptions.unshift({
      sortByOptionTitle: offersSearchResultsModel?.offersSortbyDistance,
      sortByOptionCode: 'OFFER_DISTANCE',
    });
  }

  const sessionFromServer = {
    ...ariesOffer,
    selectedBrands,
    selectedOfferCategory,
    selectedOfferType,
    selectedFilterState,
    selectedFilterCity,
    selectedFilterCountry,
    sortBy,
    AriesSessionDestination: currentDestination,
  };

  const ariesSearch = buildDestinationAriesSearch({
    destinationMetaData,
    flexibleDefaultLengthOfStay,
    fromDateIso,
    toDateIso,
    isFlexibleDateSearch: true,
  });

  await updateSessionObject({
    sessionAppDomainUrl: SESSION_APP_DOMAIN_URL,
    sessionToken,
    key: 'AriesSearch',
    value: ariesSearch,
  });
  await updateSessionObject({
    sessionAppDomainUrl: SESSION_APP_DOMAIN_URL,
    sessionToken,
    key: 'AriesOffer',
    value: sessionFromServer,
  });

  offersSearchResultsModel.isServerSideData = true;
  offersSearchResultsModel.enableDestinationOfferSSR = true;
  offersSearchResultsModel.useDestinationMetaDataPayload = true;
  offersSearchResultsModel.seoFriendlyPagination = true;
  offersSearchResultsModel.baseURL = model?.canonical;
  offersSearchResultsModel.serverApiError = serverApiError;
  offersSearchResultsModel.hasdestinationChanged = hasdestinationChanged;
  offersSearchResultsModel.processedData = {
    offerCards: mappedData?.offerCards ?? [],
    cardCount: mappedData?.cardCount ?? 0,
    cityInData: mappedData?.cityInData ?? '',
  };
  offersSearchResultsModel.facetsData = mappedData?.facetsData ?? {};
  offersSearchResultsModel.sortByData = {
    sortBy,
    sortOptions,
  };
  offersSearchResultsModel.serverDateQueryParams = {
    fromDate: fromDateQuery,
    toDate: toDateQuery,
  };
  offersSearchResultsModel.paginationData = getOffersPaginationModel(pageNo, cardsPerPage, mappedData?.cardCount ?? 0);
  offersSearchResultsModel.sessionFromServer = sessionFromServer;
  if (searchFormModel) {
    searchFormModel.numberOfSearchForms = 1;
  }
  offersSearchResultsModel.pageNo = String(pageNo);
  offersSearchResultsModel.destinationMetaData = destinationMetaData;
  offersSearchResultsModel.viewPort = viewPort;
  return model;
};

//Make pre page calls like session, datalayer, fetch model.json, producSpecifc data etc
//at server side and pass the data is passed as props to page component.
export const getServerSideProps: GetServerSideProps<any> = prePageCall(
  destinationOffersSpecificData,
  {},
  false,
  false,
  false,
  undefined,
  postAEMModelOperations
);
export default pageRenderer;
